import { memo, useImperativeHandle, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { Compartment, EditorState, Transaction } from "@codemirror/state";
import { EditorView, keymap, placeholder as editorPlaceholder } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, insertNewline } from "@codemirror/commands";
import type { Conversation } from "../types";
import { LONG_COMPOSER_TEXT, resizeComposer } from "../lib/composer-layout";

export const VIRTUAL_COMPOSER_THRESHOLD = 16_384;
export interface ComposerEditorHandle { focus: () => void }
interface EditorSeed {
  value: string;
  anchor: number;
  head: number;
  focus: boolean;
  paste?: { from: number; to: number; insert: string };
}
const configureEditor = (editor: HTMLTextAreaElement, value: string) => {
  const spellcheck = value.length <= LONG_COMPOSER_TEXT;
  if (editor.getAttribute("spellcheck") !== String(spellcheck)) {
    editor.setAttribute("spellcheck", String(spellcheck));
    editor.setAttribute("autocorrect", spellcheck ? "on" : "off");
  }
  resizeComposer(editor, value);
};

/** Short drafts use native editing. Large drafts render only visible text, including
 * gaps within huge JSON lines, so each key never lays out the entire document. */
export const ComposerInput = memo(function ComposerInput({ conversation, editorRef, readOnly, label, placeholder, onInput, onSubmit }: {
  conversation: Conversation;
  editorRef: RefObject<ComposerEditorHandle | null>;
  readOnly: boolean;
  label: string;
  placeholder: string;
  onInput: (value: string) => void;
  onSubmit: () => void;
}) {
  const nativeRef = useRef<HTMLTextAreaElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const options = useRef({ conversation, readOnly, label, placeholder, onInput, onSubmit });
  options.current = { conversation, readOnly, label, placeholder, onInput, onSubmit };
  const config = useRef(new Compartment());
  const seed = useRef<EditorSeed | null>(null);
  const composing = useRef(false);
  const [largeConversationId, setLargeConversationId] = useState<string>();
  const large = largeConversationId === conversation.id || (conversation.draft?.content.length ?? 0) > VIRTUAL_COMPOSER_THRESHOLD;
  const configuration = () => [
    EditorState.readOnly.of(options.current.readOnly),
    EditorView.editable.of(!options.current.readOnly),
    EditorView.contentAttributes.of({ id: "chat-composer", "aria-label": options.current.label,
      "aria-disabled": String(options.current.readOnly), spellcheck: "false", autocorrect: "off", autocapitalize: "sentences", enterkeyhint: "send" }),
    editorPlaceholder(options.current.placeholder),
  ];
  useImperativeHandle(editorRef, () => ({ focus: () => { if (viewRef.current) viewRef.current.focus(); else nativeRef.current?.focus(); } }), []);

  useLayoutEffect(() => {
    if (!large || !mountRef.current) return;
    setLargeConversationId(conversation.id);
    const initial = seed.current;
    seed.current = null;
    const value = initial?.value ?? options.current.conversation.draft?.content ?? "";
    const view = new EditorView({
      parent: mountRef.current,
      state: EditorState.create({ doc: value,
        selection: { anchor: initial?.anchor ?? value.length, head: initial?.head ?? value.length },
        extensions: [
          history(), EditorView.lineWrapping, config.current.of(configuration()),
          keymap.of([
            { key: "Enter", run: editor => { if (editor.composing || composing.current) return false; if (!options.current.readOnly) options.current.onSubmit(); return true; } },
            { key: "Shift-Enter", run: insertNewline },
            ...defaultKeymap, ...historyKeymap,
          ]),
          EditorView.domEventHandlers({
            compositionstart: () => { composing.current = true; },
            compositionend: () => { composing.current = false; },
          }),
          EditorView.updateListener.of(update => {
            if (update.docChanged && !update.transactions.every(transaction => transaction.annotation(Transaction.userEvent) === "external")) {
              options.current.onInput(update.state.doc.toString());
            }
          }),
        ],
      }),
    });
    viewRef.current = view;
    if (initial?.paste) {
      const { from, to, insert } = initial.paste;
      view.dispatch({ changes: { from, to, insert }, selection: { anchor: from + insert.length }, scrollIntoView: true, userEvent: "input.paste" });
    } else if (initial) options.current.onInput(value);
    if (initial?.focus) { view.focus(); view.dispatch({ effects: EditorView.scrollIntoView(view.state.selection.main.head) }); }
    return () => { view.destroy(); viewRef.current = null; };
  }, [large, conversation.id]);

  useLayoutEffect(() => {
    const view = viewRef.current;
    if (view) view.dispatch({ effects: config.current.reconfigure(configuration()) });
  }, [readOnly, label, placeholder]);

  useLayoutEffect(() => {
    const value = conversation.draft?.content ?? "";
    const view = viewRef.current;
    if (view) {
      // Buffered renders contain our latest edit; only apply external changes.
      if (view.state.doc.toString() !== value) view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: value },
        selection: { anchor: Math.min(view.state.selection.main.head, value.length) },
        annotations: [Transaction.userEvent.of("external"), Transaction.addToHistory.of(false)] });
      return;
    }
    const editor = nativeRef.current;
    if (!editor) return;
    if (editor.value !== value) editor.value = value;
    configureEditor(editor, value);
  }, [conversation]);

  useLayoutEffect(() => {
    const editor = nativeRef.current;
    if (!editor || typeof ResizeObserver === "undefined") return;
    let previousWidth = editor.clientWidth;
    const observer = new ResizeObserver(() => {
      if (editor.clientWidth === previousWidth) return;
      previousWidth = editor.clientWidth;
      resizeComposer(editor);
    });
    observer.observe(editor);
    return () => observer.disconnect();
  }, [large, conversation.id]);

  const upgrade = (editor: HTMLTextAreaElement) => {
    seed.current = { value: editor.value, anchor: editor.selectionStart, head: editor.selectionEnd, focus: document.activeElement === editor };
    setLargeConversationId(conversation.id);
  };
  if (large) return <div className="composer-virtual" ref={mountRef} />;
  return <textarea
    key={conversation.id}
    id="chat-composer"
    ref={nativeRef}
    className="composer-editor"
    dir="auto"
    role="textbox"
    aria-multiline="true"
    rows={1}
    defaultValue={conversation.draft?.content ?? ""}
    readOnly={readOnly}
    onPaste={event => {
      const insert = event.clipboardData.getData("text/plain").replace(/\r\n?/g, "\n");
      const editor = event.currentTarget;
      if (readOnly || composing.current || editor.value.length - (editor.selectionEnd - editor.selectionStart) + insert.length <= VIRTUAL_COMPOSER_THRESHOLD) return;
      // Intercept before the browser puts a huge line into a native textarea.
      event.preventDefault();
      seed.current = { value: editor.value, anchor: editor.selectionStart, head: editor.selectionEnd, focus: true,
        paste: { from: editor.selectionStart, to: editor.selectionEnd, insert } };
      setLargeConversationId(conversation.id);
    }}
    onInput={event => {
      const editor = event.currentTarget;
      if (editor.value.length > VIRTUAL_COMPOSER_THRESHOLD && !composing.current) upgrade(editor);
      else { configureEditor(editor, editor.value); onInput(editor.value); }
    }}
    onCompositionStart={() => { composing.current = true; }}
    onCompositionEnd={event => { composing.current = false; if (event.currentTarget.value.length > VIRTUAL_COMPOSER_THRESHOLD) upgrade(event.currentTarget); }}
    onKeyDown={event => {
      if (event.key !== "Enter" || event.shiftKey || composing.current || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
      event.preventDefault();
      onSubmit();
    }}
    placeholder={placeholder}
    aria-label={label}
    autoCapitalize="sentences"
    inputMode="text"
    enterKeyHint="send"
    aria-disabled={readOnly}
  />;
});
